import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabase.js";
import { removeFile } from "../lib/storage.js";

// Same shape as equipment/api.js — that module is the reference for this pattern.

const SELECT =
  "id,name,business,phone,email,home_address,billing_address,dl_number," +
  "dl_photo_path,card_type,card_last4,card_exp,coi_path,coi_name,coi_expires,notes," +
  "customer_agreements(id)";
// dl_photo_path, coi_path, and the agreement count drive the "docs on file" icons.

export function rowToForm(row = {}) {
  return {
    name: row.name ?? "",
    business: row.business ?? "",
    phone: row.phone ?? "",
    email: row.email ?? "",
    homeAddress: row.home_address ?? "",
    billingAddress: row.billing_address ?? "",
    dlNumber: row.dl_number ?? "",
    cardType: row.card_type ?? "",
    cardLast4: row.card_last4 ?? "",
    cardExp: row.card_exp ?? "",
    coiExpires: row.coi_expires ?? "",
    notes: row.notes ?? "",
    dlPhotoPath: row.dl_photo_path ?? null,
    coiPath: row.coi_path ?? null,
    coiName: row.coi_name ?? null,
  };
}

const blankToNull = (v) => (v === "" || v === undefined ? null : v);

export function formToRow(f) {
  return {
    name: f.name.trim(),
    business: blankToNull(f.business),
    phone: blankToNull(f.phone),
    email: f.email.trim(),
    home_address: blankToNull(f.homeAddress),
    billing_address: blankToNull(f.billingAddress),
    dl_number: blankToNull(f.dlNumber),
    card_type: blankToNull(f.cardType),
    // Schema enforces ^\d{0,4}$ — never a full card number. The UI strips
    // non-digits and caps at 4, and this is the second line of defence.
    card_last4: blankToNull(String(f.cardLast4 ?? "").replace(/\D/g, "").slice(0, 4)),
    card_exp: blankToNull(f.cardExp),
    coi_expires: blankToNull(f.coiExpires),
    notes: blankToNull(f.notes),
    dl_photo_path: f.dlPhotoPath ?? null,
    coi_path: f.coiPath ?? null,
    coi_name: f.coiName ?? null,
  };
}

export function useCustomers() {
  return useQuery({
    queryKey: ["customers"],
    queryFn: async () => {
      const { data, error } = await supabase.from("customers").select(SELECT).order("name");
      if (error) throw new Error(error.message);
      return data;
    },
  });
}

/** Only what the balance and rental-count columns need. */
export function useRentalsForCustomers() {
  return useQuery({
    queryKey: ["rentals", "for-customers"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("rentals")
        .select("id,customer_id,status,paid,total");
      if (error) throw new Error(error.message);
      return data;
    },
  });
}

export function useSaveCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, form, previous }) => {
      const row = formToRow(form);
      const q = id
        ? supabase.from("customers").update(row).eq("id", id).select("id")
        : supabase.from("customers").insert([row]).select("id");
      const { data, error } = await q;
      if (error) {
        if (error.code === "23514") {
          throw new Error(
            "The card field only accepts the last 4 digits. Never enter a full card number."
          );
        }
        throw new Error(error.message);
      }
      if (!data?.length) {
        throw new Error(
          "The database accepted the request but changed nothing — usually a row " +
            "level security policy."
        );
      }
      // Only after the row is safely saved — see the note in equipment/api.js.
      for (const key of ["dl_photo_path", "coi_path"]) {
        const before = previous?.[key];
        if (before && before !== row[key]) await removeFile(before);
      }

      return data[0];
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["customers"] }),
  });
}

export function useDeleteCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, paths = [] }) => {
      // Agreement rows cascade with the customer, but their stored files do not
      // — storage has no foreign keys. Collect the paths before the row goes.
      const { data: agreements } = await supabase
        .from("customer_agreements")
        .select("file_path")
        .eq("customer_id", id);

      const { data, error } = await supabase.from("customers").delete().eq("id", id).select("id");
      if (error) {
        if (error.code === "23503") {
          throw new Error(
            "This customer has rentals attached, so deleting them would orphan that " +
              "history. Keep the record instead — it is what the invoices point at."
          );
        }
        throw new Error(error.message);
      }
      if (!data?.length) throw new Error("Nothing was deleted — a security policy may have blocked it.");
      // Driver's licence, COI, and agreement files are unreachable once the row is gone.
      const all = [...paths, ...(agreements ?? []).map((a) => a.file_path)].filter(Boolean);
      for (const p of all) await removeFile(p);
      return id;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["customers"] }),
  });
}

/** Ported from v10: cancelled rentals do not count, unpaid ones total up. */
export function customerBalance(customerId, rentals = []) {
  const mine = rentals.filter((r) => r.customer_id === customerId && r.status !== "cancelled");
  const owed = mine
    .filter((r) => !r.paid)
    .reduce((sum, r) => sum + (Number(r.total) || 0), 0);
  return { count: mine.length, owed };
}
